import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  BookOpen, Upload, Download, Trash2, FileText, Loader2, Search, Plus,
  Building2, User, Users as UsersIcon, ChevronDown, AlertTriangle, PenLine, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { SignatureFieldEditor } from '../components/SignatureFieldEditor';
import { HelpButton } from '../components/HelpButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPRESAS = [
  { key: 'edp', label: 'EDP' },
  { key: 'galp', label: 'Galp' },
  { key: 'endesa', label: 'Endesa' },
  { key: 'iberdrola', label: 'Iberdrola' },
  { key: 'gold', label: 'Gold Energy' },
  { key: 'repsol', label: 'Repsol' },
  { key: 'yes', label: 'Yes' },
  { key: 'nos', label: 'NOS' },
  { key: 'meo', label: 'MEO' },
  { key: 'vodafone', label: 'Vodafone' },
  { key: 'nos_securitas', label: 'NOS Securitas' },
  { key: 'prosegur', label: 'Prosegur' },
];

const TIPOS_CLIENTE = [
  { key: 'particular', label: 'Particular', icon: User },
  { key: 'empresa', label: 'Empresa', icon: Building2 },
  { key: 'ambos', label: 'Ambos', icon: UsersIcon },
];

const empresaLabel = (key) => EMPRESAS.find((e) => e.key === key)?.label || key;
const tipoLabel = (key) => TIPOS_CLIENTE.find((t) => t.key === key)?.label || key;

const formatBytes = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i ? 1 : 0)} ${u[i]}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' }); } catch { return ''; }
};

const isNew = (it) => it.aviso_tipo && it.aviso_tipo !== 'nenhum' && it.created_at && (Date.now() - new Date(it.created_at).getTime()) < 7 * 86400000;

export const KnowledgeBase = () => {
  const { user } = useAuth();
  const canManage = ['super_admin', 'gestor', 'admin'].includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const location = useLocation();
  const fileParam = new URLSearchParams(location.search).get('file');
  const [filterEmpresa, setFilterEmpresa] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');

  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ empresa: '', tipo_cliente: 'ambos', files: [], aviso_tipo: 'formulario', substitui_id: 'none' });
  const [sigEditor, setSigEditor] = useState(null); // {id, name}

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedKb, setSelectedKb] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  const toggleKb = (id) => setSelectedKb((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleBulkDelete = async () => {
    try {
      const res = await axios.post(`${API_URL}/knowledge/bulk-delete`, { file_ids: Array.from(selectedKb) });
      toast.success(`${res.data.count} documento(s) apagado(s)`);
      setSelectedKb(new Set());
      setConfirmBulk(false);
      fetchItems();
    } catch (e) {
      const st = e?.response?.status;
      toast.error(`Falha ao apagar documentos${st ? ` (erro ${st})` : ''}: ${e?.response?.data?.detail || e?.message || 'sem resposta do servidor'}`);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/knowledge`);
      setItems(res.data.items || []);

    } catch (err) {
      toast.error('Erro ao carregar ficheiros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fileParam || !items.length) return;
    const hit = items.find((x) => x.id === fileParam);
    if (hit) {
      setSearch(hit.name);
      axios.post(`${API_URL}/knowledge/news/seen`, { ids: [hit.id] }).catch(() => {});
    }
  }, [fileParam, items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filterEmpresa !== 'all' && it.empresa !== filterEmpresa) return false;
      if (filterTipo !== 'all' && it.tipo_cliente !== filterTipo) return false;
      if (q && !(it.name || '').toLowerCase().includes(q) && !(it.original_filename || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, filterEmpresa, filterTipo]);

  // Group: empresa -> tipo_cliente -> [files]
  const grouped = useMemo(() => {
    const out = {};
    for (const it of filtered) {
      const e = it.empresa || 'outros';
      const t = it.tipo_cliente || 'ambos';
      if (!out[e]) out[e] = {};
      if (!out[e][t]) out[e][t] = [];
      out[e][t].push(it);
    }
    return out;
  }, [filtered]);

  const openUpload = () => {
    setForm({ empresa: '', tipo_cliente: 'ambos', files: [], aviso_tipo: 'formulario', substitui_id: 'none' });
    setShowUpload(true);
  };

  const submitUpload = async () => {
    if (!form.empresa) { toast.error('Selecione a empresa'); return; }
    if (!form.files || form.files.length === 0) { toast.error('Selecione pelo menos um ficheiro'); return; }
    try {
      setUploading(true);
      const fd = new FormData();
      Array.from(form.files).forEach((f) => fd.append('files', f));
      fd.append('empresa', form.empresa);
      fd.append('tipo_cliente', form.tipo_cliente);
      fd.append('aviso_tipo', form.aviso_tipo);
      if (form.substitui_id !== 'none') fd.append('substitui_id', form.substitui_id);
      const res = await axios.post(`${API_URL}/knowledge/upload-batch`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ok = res.data?.count || 0;
      const errs = res.data?.errors || [];
      if (ok > 0) toast.success(`${ok} ficheiro(s) carregado(s)`);
      if (errs.length > 0) toast.error(`${errs.length} ficheiro(s) falharam`);
      setShowUpload(false);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (it) => {
    try {
      const res = await axios.get(`${API_URL}/knowledge/${it.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: it.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = it.original_filename || it.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erro ao descarregar');
    }
  };

  const deleteItem = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_URL}/knowledge/${confirmDelete.id}`);
      toast.success('Ficheiro eliminado');
      setConfirmDelete(null);
      fetchItems();
    } catch (err) {
      toast.error('Erro ao eliminar');
    }
  };

  // Determine which groups are open. With filters applied, auto-open all.
  const hasActiveFilter = (search.trim().length > 0) || filterEmpresa !== 'all' || filterTipo !== 'all';
  const [openEmpresas, setOpenEmpresas] = useState({});
  const [openTipos, setOpenTipos] = useState({});
  const isEmpresaOpen = (key) => hasActiveFilter || !!openEmpresas[key];
  const isTipoOpen = (empKey, tipoKey) => hasActiveFilter || !!openTipos[`${empKey}::${tipoKey}`];
  const toggleEmpresa = (key) => setOpenEmpresas((p) => ({ ...p, [key]: !p[key] }));
  const toggleTipo = (empKey, tipoKey) => {
    const k = `${empKey}::${tipoKey}`;
    setOpenTipos((p) => ({ ...p, [k]: !p[k] }));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="knowledge-base-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#2D3E6F]" />
              Base de Conhecimento
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Formulários e tabelas de preços das campanhas em vigor
            </p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <HelpButton category="base-conhecimento" />
              <Button
                onClick={openUpload}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2"
                data-testid="kb-upload-btn"
              >
                <Plus className="w-4 h-4" /> Adicionar Ficheiro
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="kb-search"
                />
              </div>
              <Select value={filterEmpresa} onValueChange={setFilterEmpresa}>
                <SelectTrigger data-testid="kb-filter-empresa">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas</SelectItem>
                  {EMPRESAS.map((e) => (
                    <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger data-testid="kb-filter-tipo">
                  <SelectValue placeholder="Tipo de Cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {TIPOS_CLIENTE.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Loading / empty */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="text-center py-16">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Sem ficheiros disponíveis</p>
              {canManage && (
                <p className="text-slate-400 text-sm mt-1">
                  Clica em "Adicionar Ficheiro" para carregar a primeira campanha.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Grouped collapsible view: Empresa > Tipo Cliente */
          <div className="space-y-3">
            {canManage && selectedKb.size > 0 && (
              <div className="flex items-center justify-between bg-red-600 text-white rounded-xl px-4 py-2.5 sticky top-2 z-20 shadow-lg" data-testid="kb-bulk-bar">
                <span className="text-sm font-medium">{selectedKb.size} documento(s) selecionado(s)</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/15 h-8" onClick={() => setSelectedKb(new Set())} data-testid="kb-bulk-clear">Limpar</Button>
                  <Button size="sm" className="bg-white text-red-600 hover:bg-slate-100 h-8" onClick={() => setConfirmBulk(true)} data-testid="kb-bulk-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Apagar selecionados
                  </Button>
                </div>
              </div>
            )}
            {Object.entries(grouped).map(([empKey, byTipo]) => {
              const totalCount = Object.values(byTipo).reduce((s, arr) => s + arr.length, 0);
              const empOpen = isEmpresaOpen(empKey);
              return (
                <Card key={empKey} className="rounded-2xl border-slate-100 overflow-hidden" data-testid={`kb-group-${empKey}`}>
                  <Collapsible open={empOpen} onOpenChange={() => toggleEmpresa(empKey)}>
                    <CollapsibleTrigger
                      className="flex items-center justify-between w-full px-5 py-4 hover:bg-slate-50 transition-colors"
                      data-testid={`kb-empresa-toggle-${empKey}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="inline-block w-2 h-2 rounded-full bg-[#4DD4E8]" />
                        <h2 className="text-base font-semibold text-slate-900">{empresaLabel(empKey)}</h2>
                        <span className="text-xs text-slate-400 font-medium">({totalCount} ficheiro{totalCount !== 1 ? 's' : ''})</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${empOpen ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-5 pb-4 space-y-2 border-t border-slate-100">
                        {Object.entries(byTipo).map(([tipoKey, files]) => {
                          const TipoIcon = TIPOS_CLIENTE.find((t) => t.key === tipoKey)?.icon || UsersIcon;
                          const tipoOpen = isTipoOpen(empKey, tipoKey);
                          return (
                            <Collapsible
                              key={tipoKey}
                              open={tipoOpen}
                              onOpenChange={() => toggleTipo(empKey, tipoKey)}
                            >
                              <CollapsibleTrigger
                                className="flex items-center justify-between w-full px-3 py-2 mt-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                                data-testid={`kb-tipo-toggle-${empKey}-${tipoKey}`}
                              >
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                                  <TipoIcon className="w-3.5 h-3.5" />
                                  {tipoLabel(tipoKey)}
                                  <span className="text-slate-400 normal-case font-normal">({files.length})</span>
                                </div>
                                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${tipoOpen ? 'rotate-180' : ''}`} />
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 pl-2">
                                  {files.map((it) => (
                                    <div
                                      key={it.id}
                                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors group border ${selectedKb.has(it.id) ? 'bg-red-50 border-red-200' : 'bg-white hover:bg-slate-50 border-slate-100'}`}
                                      data-testid={`kb-item-${it.id}`}
                                    >
                                      {canManage && (
                                        <input
                                          type="checkbox"
                                          checked={selectedKb.has(it.id)}
                                          onChange={() => toggleKb(it.id)}
                                          className="w-4 h-4 accent-[#2D3E6F] flex-shrink-0"
                                          data-testid={`kb-select-${it.id}`}
                                        />
                                      )}
                                      <div className="p-2 rounded-md bg-slate-50 shadow-sm">
                                        <FileText className="w-4 h-4 text-[#2D3E6F]" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate" title={it.name}>
                                          {it.name}
                                          {isNew(it) && (
                                            <span className="ml-2 inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500 text-white align-middle" data-testid={`kb-new-${it.id}`}>
                                              NOVO
                                            </span>
                                          )}
                                          {it.signable && (
                                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 align-middle">
                                              <PenLine className="w-2.5 h-2.5" /> Assinável
                                            </span>
                                          )}
                                        </p>
                                        <p className="text-xs text-slate-400 truncate" title={it.original_filename}>
                                          {it.original_filename} · {formatBytes(it.size)}
                                        </p>
                                        <p className="text-[11px] text-slate-400 flex items-center gap-1 truncate mt-0.5">
                                          <CalendarClock className="w-3 h-3 flex-shrink-0" />
                                          Carregado a {fmtDateTime(it.created_at)}{it.uploaded_by_name ? ` · por ${it.uploaded_by_name}` : ''}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {canManage && (it.content_type || '').toLowerCase().includes('pdf') && (
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setSigEditor({ id: it.id, name: it.name })}
                                            title="Configurar assinatura"
                                            data-testid={`kb-sign-config-${it.id}`}
                                            className={`h-8 w-8 ${it.signable ? 'text-indigo-600' : 'text-slate-400'} hover:text-indigo-700`}
                                          >
                                            <PenLine className="w-4 h-4" />
                                          </Button>
                                        )}
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          onClick={() => downloadFile(it)}
                                          title="Descarregar"
                                          data-testid={`kb-download-${it.id}`}
                                          className="h-8 w-8 text-slate-500 hover:text-[#2D3E6F]"
                                        >
                                          <Download className="w-4 h-4" />
                                        </Button>
                                        {canManage && (
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setConfirmDelete(it)}
                                            title="Eliminar"
                                            data-testid={`kb-delete-${it.id}`}
                                            className="h-8 w-8 text-slate-500 hover:text-red-600"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload modal */}
      <Dialog open={showUpload} onOpenChange={(o) => { if (!uploading) setShowUpload(o); }}>
        <DialogContent data-testid="kb-upload-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#2D3E6F]" />
              Adicionar Ficheiro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              Seleciona um ou vários ficheiros. O nome de cada ficheiro será usado como título.
              A <strong>empresa</strong> e o <strong>tipo de cliente</strong> são aplicados a todos os ficheiros do batch.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Empresa *</Label>
                <Select value={form.empresa} onValueChange={(v) => setForm({ ...form, empresa: v, substitui_id: 'none' })}>
                  <SelectTrigger data-testid="kb-form-empresa"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                  <SelectContent>
                    {EMPRESAS.map((e) => (
                      <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de Cliente</Label>
                <Select value={form.tipo_cliente} onValueChange={(v) => setForm({ ...form, tipo_cliente: v })}>
                  <SelectTrigger data-testid="kb-form-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_CLIENTE.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Avisar os comerciais</Label>
                <Select value={form.aviso_tipo} onValueChange={(v) => setForm({ ...form, aviso_tipo: v })}>
                  <SelectTrigger data-testid="kb-form-aviso"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="formulario">📄 Novo formulário de contratação</SelectItem>
                    <SelectItem value="tarifarios">💶 Novos tarifários / preços</SelectItem>
                    <SelectItem value="nenhum">Sem aviso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Substitui o modelo anterior</Label>
                <Select value={form.substitui_id} onValueChange={(v) => setForm({ ...form, substitui_id: v })} disabled={!form.empresa}>
                  <SelectTrigger data-testid="kb-form-substitui"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (não substitui)</SelectItem>
                    {items.filter((it) => it.empresa === form.empresa).map((it) => (
                      <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.substitui_id !== 'none' && <p className="text-[11px] text-amber-700 mt-1">O ficheiro antigo será arquivado e deixa de aparecer.</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="kb-file">Ficheiros *</Label>
              <Input
                id="kb-file"
                type="file"
                data-max-mb="0"
                multiple
                onChange={(e) => {
                  const MAX_SIZE_MB = 10;
                  const all = Array.from(e.target.files || []);
                  const valid = [];
                  const tooLarge = [];
                  all.forEach((f) => {
                    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
                      tooLarge.push({ name: f.name, sizeMB: (f.size / 1024 / 1024).toFixed(1) });
                    } else {
                      valid.push(f);
                    }
                  });
                  tooLarge.forEach((r) => {
                    toast.error(`Ficheiro demasiado grande: "${r.name}" (${r.sizeMB} MB). Máximo permitido: ${MAX_SIZE_MB} MB.`, { duration: 6000 });
                  });
                  setForm({ ...form, files: valid });
                  if (tooLarge.length > 0) {
                    e.target.value = ''; // limpa o input para o user poder voltar a tentar
                  }
                }}
                data-testid="kb-form-file"
              />
              {form.files && form.files.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2 space-y-1">
                  {form.files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs gap-2">
                      <span className="truncate text-slate-700" title={f.name}>{f.name}</span>
                      <span className="text-slate-400 flex-shrink-0">{formatBytes(f.size)}</span>
                    </div>
                  ))}
                  <p className="text-xs text-slate-500 pt-1 border-t border-slate-200">
                    Total: {form.files.length} ficheiro(s)
                  </p>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1">Aceita qualquer formato (PDF, Excel, Word, imagens...). Máx 50MB por ficheiro.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)} disabled={uploading}>Cancelar</Button>
            <Button
              onClick={submitUpload}
              disabled={uploading}
              className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
              data-testid="kb-form-submit"
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A carregar...</> : <><Upload className="w-4 h-4 mr-2" />Carregar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="kb-delete-dialog">
          <AlertDialogHeader>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <AlertDialogTitle className="text-red-700">
                  Atenção — eliminar tarifário?
                </AlertDialogTitle>
                <AlertDialogDescription className="mt-2 text-slate-600">
                  Vais eliminar definitivamente o ficheiro{' '}
                  <span className="font-semibold text-slate-900">"{confirmDelete?.name}"</span>{' '}
                  ({empresaLabel(confirmDelete?.empresa)} · {tipoLabel(confirmDelete?.tipo_cliente)}).
                  <br /><br />
                  Esta ação <strong>não pode ser desfeita</strong>. O ficheiro deixará de estar disponível para descarga
                  por todos os utilizadores.
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel data-testid="kb-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteItem}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="kb-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Sim, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent data-testid="kb-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Apagar {selectedKb.size} documento(s)?</AlertDialogTitle>
            <AlertDialogDescription className="mt-2 text-slate-600">
              Vais arquivar <strong>{selectedKb.size}</strong> documento(s) selecionado(s). Deixarão de aparecer na Base de Conhecimento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 focus:ring-red-600" data-testid="kb-bulk-delete-confirm">
              <Trash2 className="w-4 h-4 mr-2" /> Sim, apagar todos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {sigEditor && (
        <SignatureFieldEditor
          documentId={sigEditor.id}
          documentName={sigEditor.name}
          open={!!sigEditor}
          onClose={() => setSigEditor(null)}
          onSaved={fetchItems}
        />
      )}
    </Layout>
  );
};

export default KnowledgeBase;
